import React, { FC, useCallback, useMemo } from "react";
import styled from "styled-components";

import FormField from "@ingka/form-field";
import Combobox, { MenuItem } from "@ingka/combobox";
import Skeleton from "@ingka/skeleton";
import { fontSize75 } from "@ingka/variables/design-tokens";
import { getImage } from "../../utils/markets";

interface Market {
	key: string;
	name: string;
}

interface SelectMarketsProps {
	marketsData?: Market[];
	label: string;
	helperText?: string;
	validationText?: string;
	onChange: (value: string[]) => void;
	selected?: string | string[];
	loading?: boolean;
	multiple?: boolean;
	disabled?: boolean;
	id: string;
	checkKeys?: boolean;
}

const StyledCombobox = styled(Combobox)`
	.combobox__label {
		font-size: ${fontSize75};
	}
	.menu-item__title {
		display: flex;
		align-items: center;
	}
	img {
		height: 15px;
		width: 15px;
		margin-right: 10px;
	}
`;

const SkeletonWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.2rem;
	margin: 1rem 0 1rem 0;
`;

const SelectMarkets: FC<SelectMarketsProps> = ({
	id,
	loading,
	marketsData = [],
	label,
	helperText,
	validationText = "",
	selected,
	multiple = true,
	disabled = false,
	onChange,
	checkKeys = false,
}) => {
	const selectedArray = useMemo(() => {
		if (!selected) return [];
		return Array.isArray(selected) ? selected : [selected];
	}, [selected]);

	const helperMsg = useMemo(() => {
		return selectedArray.length > 0 ? `Selected: ${selectedArray.join(", ")}` : helperText || "";
	}, [helperText, selectedArray]);

	const values = useMemo(
		() =>
			checkKeys
				? marketsData.filter((item) => selectedArray.includes(item.key)).map((item) => item.key)
				: marketsData.filter((item) => selectedArray.includes(item.name)).map((item) => item.key),
		[marketsData, selectedArray, checkKeys]
	);

	const handleChange = useCallback(
		(marketKeys: string[]) => {
			const marketNames = checkKeys
				? marketsData.filter((item) => marketKeys.includes(item.key)).map((item) => item.key)
				: marketsData.filter((item) => marketKeys.includes(item.key)).map((item) => item.name);
			onChange(marketNames);
		},
		[marketsData, onChange, checkKeys]
	);

	const handleClear = useCallback(() => {
		return onChange([]);
	}, [onChange]);

	const hasMarkets = marketsData.length > 0;
	const shouldValidate = Boolean(validationText || !hasMarkets);
	const validationType = !hasMarkets ? "warning" : "error";
	const validationMessage = !hasMarkets ? "Seems like the application does not have any markets" : validationText;

	if (loading) {
		return (
			<SkeletonWrapper>
				<Skeleton width="10rem" />
				<Skeleton height="2.8rem" />
			</SkeletonWrapper>
		);
	}

	return (
		<>
			<FormField
				{...(helperMsg && {
					fieldHelper: {
						id: `${id}-helper-msg`,
						msg: helperMsg,
					},
				})}
				shouldValidate={shouldValidate}
				valid={validationType}
				validation={{
					id: `${id}-error-msg`,
					msg: validationMessage,
					type: validationType,
				}}
			>
				<StyledCombobox
					id={id}
					multiple={multiple}
					label={label}
					labelId={`${id}-select-label`}
					listboxId={`${id}-combobox`}
					onClearClicked={handleClear}
					onListBoxChange={handleChange}
					placeholder="Choose an option"
					value={values}
					disabled={disabled || !hasMarkets}
				>
					{marketsData.map((market, index) => (
						<MenuItem
							id={market.key}
							key={`${market.key}_${index}`}
							title={
								<>
									<img src={getImage(market.key) || getImage("xx")} alt="" />
									<span>{market.name}</span>
								</>
							}
						/>
					))}
				</StyledCombobox>
			</FormField>
		</>
	);
};

export default SelectMarkets;
